/*
 * -----------------------------------------------------------------------
 *  <copyright file="import-library-controls.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import Constants from '../constants';
import { NamedItemFields as NamedItemFieldsWithAudit } from 'queries/generated/fragments.generated';
import { NamedItemType, TimeFrequencyType } from 'types/graphql-global-types';
import { checkData, checkLine, readEnum, readLov, readNumber, readString } from 'utils/importers/helpers';
import doImport from 'utils/importers/import-file';

const importLibraryControls = (data: any, lovs: Exclude<NamedItemFieldsWithAudit, 'auditId'>[], isSystemLevel: any) => {
    const controls: any = [];
    const errors: any = [];
    if (!checkData(data, errors)) return { data: [], errors };

    data.forEach((line: any, index: number) => {
        if (!checkLine(line, index, errors)) return;

        const initialErrorCount = errors.length;

        const controlPhaseId = isSystemLevel
            ? Constants.ObjectId.Empty
            : readLov('Control Phase', lovs, NamedItemType.CONTROL_PHASE, line, 0, index + 1, errors, false);

        const controlTypeId = isSystemLevel
            ? Constants.ObjectId.Empty
            : readLov('Control Type', lovs, NamedItemType.CONTROL_TYPE, line, 1, index + 1, errors);

        const description = readString('Description', line, isSystemLevel ? 0 : 2, index + 1, errors, 3000, true);
        const action = readString('Action to be Taken', line, isSystemLevel ? 1 : 3, index + 1, errors, 3000);
        const threshold = readString('Threshold', line, isSystemLevel ? 2 : 4, index + 1, errors, 3000);

        const responsibleFunctionId = isSystemLevel
            ? Constants.ObjectId.Empty
            : readLov('Responsible Function', lovs, NamedItemType.RESPONSIBLE_FUNCTION, line, 5, index + 1, errors);

        const errorsBeforeReviewFrequency = errors.length;

        let reviewFrequencyPeriod = readNumber('Review Frequency Unit', line, isSystemLevel ? 3 : 6, index + 1, errors);
        if (
            reviewFrequencyPeriod !== null &&
            !isNaN(reviewFrequencyPeriod) &&
            (reviewFrequencyPeriod <= 0 || reviewFrequencyPeriod > 31)
        ) {
            errors.push({
                line: index + 1,
                message: `Invalid review frequency units (${reviewFrequencyPeriod}) - must be between 1-31.`,
            });
            reviewFrequencyPeriod = null;
        }
        const reviewFrequencyUnits = readEnum(
            'Review Frequency Period',
            [
                TimeFrequencyType.DAILY,
                TimeFrequencyType.WEEKLY,
                TimeFrequencyType.MONTHLY,
                TimeFrequencyType.QUARTERLY,
                TimeFrequencyType.ANNUALLY,
            ],
            line,
            isSystemLevel ? 4 : 7,
            index + 1,
            errors,
        );

        // Either both period and units exist, or neither exist
        if (
            errors.length === errorsBeforeReviewFrequency &&
            ((reviewFrequencyPeriod !== null && !reviewFrequencyUnits) ||
                (reviewFrequencyPeriod === null && reviewFrequencyUnits))
        )
            errors.push({ line: index + 1, message: 'Both period and units must be populated, or empty.' });

        if (errors.length !== initialErrorCount) return;
        controls.push({
            id: null,
            active: true,
            description,
            action,
            threshold,
            responsibleFunctionId,
            controlPhaseId,
            controlTypeId,
            reviewFrequencyPeriod,
            reviewFrequencyUnits,
        });
    });

    return { data: controls, errors };
};

export default (file: any, lovs: Exclude<NamedItemFieldsWithAudit, 'auditId'>[], isSystemLevel: any) =>
    doImport({ file, lovs, isSystemLevel, importer: importLibraryControls });
